import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Typography, notification } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { listAllPublicShares, listPublicShares, sharedItemCount, revokePublicShare, publicShareUrl } from "./api";
import type { Album, MediaEntry, PublicShare } from "./types";
import { EmptyState, ErrorState, LoadingState } from "@dowty/ui";

const { Text } = Typography;

const timeLeft = (expiresAtEpochSec: number, isGerman: boolean): string => {
    const hours = Math.round((expiresAtEpochSec * 1000 - Date.now()) / 3_600_000);
    if (hours <= 0) return isGerman ? "läuft jetzt ab" : "expiring now";
    if (hours < 24) return isGerman ? `noch ${hours} Std.` : `${hours}h left`;
    return isGerman ? `noch ${Math.round(hours / 24)} Tage` : `${Math.round(hours / 24)}d left`;
};

/**
 * Every public link this user has handed out, in one place, with a way to turn each one off.
 *
 * Lives on the albums page rather than only inside an album because that's where someone looks when the
 * thought is "I shared the wrong thing" — by then they've closed the dialog the link came from and may
 * not remember which album it was cut from. Listing is scoped by who created each link, not by album
 * visibility, so a link stays findable and revocable regardless of what happened to the album since.
 */
export const ShareLinksModal: React.FC<{
    open: boolean;
    onClose: () => void;
    albums?: Album[];
    albumId?: number;
    items?: MediaEntry[];
}> = ({ open, onClose, albums, albumId, items }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const [api, contextHolder] = notification.useNotification();
    const [shares, setShares] = useState<PublicShare[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [revokingId, setRevokingId] = useState<number | null>(null);
    const token = useAuth().user?.token;

    useEffect(() => {
        if (!open || !token) return;
        setShares(null);
        setLoadError(null);
        let cancelled = false;
        (albumId === undefined ? listAllPublicShares(token) : listPublicShares(token, albumId))
            .then(rows => { if (!cancelled) setShares(rows); })
            .catch(err => {
                if (!cancelled) setLoadError((err as Error).message || (isGerman ? "Freigabelinks konnten nicht geladen werden." : "Couldn't load your share links."));
            });
        return () => { cancelled = true; };
    }, [open, token, reloadKey, isGerman, albumId]);

    const revoke = async (share: PublicShare) => {
        if (!token) return;
        setRevokingId(share.shareId);
        try {
            await revokePublicShare(token, share.shareId);
            setShares(prev => (prev ?? []).filter(s => s.shareId !== share.shareId));
            api.success({ message: isGerman ? "Link deaktiviert" : "Link turned off", description: isGerman ? "Dieser Link funktioniert ab sofort nicht mehr." : "Opening it now shows that it no longer works.", placement: "top" });
        } catch (err) {
            api.error({ message: isGerman ? "Link konnte nicht deaktiviert werden" : "Couldn't turn off that link", description: (err as Error).message, placement: "top" });
        } finally {
            setRevokingId(null);
        }
    };

    const albumName = (albumId: number): string => {
        const album = albums?.find(a => a.id === albumId);
        if (!album) return isGerman ? "ein nicht mehr verfügbares Album" : "an album you no longer have";
        return album.label || `${album.host.replace(/^https?:\/\//, "")}/${album.sharingId}`;
    };

    const [allTitle, albumTitle] = isGerman ? ["Deine Freigabelinks", "Freigabelinks für dieses Album"] : ["Your share links", "Share links for this album"];
    let units = ["file", "files"];
    if (items) units = ["item", "items"];
    if (isGerman) units = ["Datei", "Dateien"];

    return (
        <Modal open={open} onCancel={onClose} title={albumId === undefined ? allTitle : albumTitle} footer={[<Button key="close" onClick={onClose}>{isGerman ? "Schließen" : "Close"}</Button>]} width={580}>
            {contextHolder}
            {shares === null && !loadError && (
                <LoadingState variant="list" label={isGerman ? "Freigabelinks werden geladen" : "Loading share links"} />
            )}
            {loadError && <ErrorState message={loadError} retry={() => setReloadKey(key => key + 1)} retryLabel={isGerman ? "Erneut versuchen" : "Retry"} />}
            {!loadError && shares?.length === 0 && (
                <EmptyState title={isGerman ? "Keine aktiven Links" : "No active links"} description={isGerman ? "Wähle im Album Dateien aus und nutze Freigabelink." : "Open an album, select some items, and use “Share link”."} />
            )}
            {!loadError && shares !== null && shares.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {shares.map(share => {
                        const count = items ? sharedItemCount(items, share) : share.itemIds.length;
                        return (
                        <div key={share.shareId} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 0, flexBasis: 240 }}>
                                <Input aria-label={isGerman ? "Freigabelink" : "Share link"} readOnly size="small" value={publicShareUrl(share.shareToken)} onFocus={e => e.target.select()} />
                                <Text type="secondary" style={{ fontSize: 12, overflowWrap: "anywhere" }}>
                                    {albums && <>{albumName(share.albumId)} · </>}{count} {units[count === 1 ? 0 : 1]} · {timeLeft(share.expiresAtEpochSec, isGerman)}
                                </Text>
                            </div>
                            <Button
                                size="small"
                                aria-label={isGerman ? "Freigabelink kopieren" : "Copy share link"}
                                icon={<CopyOutlined />}
                                onClick={() => {
                                    navigator.clipboard?.writeText(publicShareUrl(share.shareToken))
                                        .then(() => api.success({ message: isGerman ? "Link kopiert" : "Link copied", placement: "top" }))
                                        .catch(() => api.info({ message: isGerman ? "Kopiere den Link aus dem Feld" : "Copy it from the field", placement: "top" }));
                                }}
                            />
                            <Button danger loading={revokingId === share.shareId} onClick={() => revoke(share)}>
                                {isGerman ? "Freigabe beenden" : "Stop sharing"}
                            </Button>
                        </div>
                        );
                    })}
                </div>
            )}
            {/* No "change expiry" control: expiry is stored per link, so extending means re-minting, which off + new link already does. */}
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 16 }}>
                {isGerman ? "Deaktivierte Links funktionieren sofort nicht mehr. Abgelaufene Links verschwinden automatisch. Für eine längere Gültigkeit deaktiviere den Link und erstelle einen neuen mit der gewünschten Dauer." : "Turning a link off takes effect immediately, however long it had left. Expired links drop off this list on their own. To change how long a link lasts, turn it off and create a new one with the duration you want."}
            </Text>
        </Modal>
    );
};
